import React, { useState, useMemo } from 'react';
import useSceneStore from '../store/useSceneStore';
import useFurnitureStore from '../store/useFurnitureStore';
import { FURNITURE_CATALOG, CATEGORY_COLORS, getAllItems, searchItems } from '../catalog/furnitureCatalog';
import { TOOLS } from '../core/constants';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';

const { FiSearch, FiGrid, FiList, FiX, FiPlus } = FiIcons;

const FurnitureCatalogPanel = () => {
  const { tool, setTool } = useSceneStore();
  const { addFurnitureItem } = useFurnitureStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('kitchen');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [isDragging, setIsDragging] = useState(null);

  const filteredItems = useMemo(() => {
    let items = selectedCategory === 'all' 
      ? getAllItems() 
      : Object.values(FURNITURE_CATALOG[selectedCategory]?.subcategories || {})
          .flatMap(subcat => Object.values(subcat.items || []));
    
    if (searchQuery) {
      items = searchItems(searchQuery);
    }
    return items;
  }, [selectedCategory, searchQuery]);

  if (tool !== TOOLS.FURNITURE) return null;

  const handleDragStart = (e, item) => {
    setIsDragging(item);
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('furnitureItemId', item.id);
    
    const dragImage = document.createElement('div');
    dragImage.className = 'p-2 bg-white border-2 border-[#1E2937] rounded-lg shadow-lg';
    dragImage.innerHTML = `
      <div class="text-sm font-medium">${item.name}</div>
      <div class="text-xs text-gray-500">${item.dimensions.width}×${item.dimensions.depth}cm</div>
    `;
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    
    setTimeout(() => {
      document.body.removeChild(dragImage);
    }, 0);
  };

  const handleDragEnd = () => {
    setIsDragging(null);
  };

  const handleItemClick = (item) => {
    console.log("Selected item:", item.name);
  };

  return (
    <div className="absolute left-20 bottom-4 w-80 bg-white rounded-xl shadow-lg border border-gray-200 flex flex-col overflow-hidden z-10" style={{ maxHeight: 'calc(100vh - 100px)' }}>
      {/* Header */}
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-700 text-sm">Katalog Mebli</h3>
          <div className="flex items-center gap-1">
            <button 
              onClick={() => setViewMode('grid')}
              className={clsx(
                "p-1.5 rounded transition-colors",
                viewMode === 'grid' ? "bg-gray-200 text-[#1E2937]" : "text-gray-400 hover:text-gray-600"
              )}
            >
              <SafeIcon icon={FiGrid} className="text-sm" />
            </button>
            <button 
              onClick={() => setViewMode('list')}
              className={clsx(
                "p-1.5 rounded transition-colors",
                viewMode === 'list' ? "bg-gray-200 text-[#1E2937]" : "text-gray-400 hover:text-gray-600"
              )}
            >
              <SafeIcon icon={FiList} className="text-sm" />
            </button>
          </div>
        </div>
        
        {/* Search */}
        <div className="relative">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input 
            type="text" 
            placeholder="Szukaj mebli..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent"
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <SafeIcon icon={FiX} className="text-sm" />
            </button>
          )}
        </div>
      </div>

      {/* Category Tabs */}
      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
        <div className="flex gap-1 overflow-x-auto pb-2 scrollbar-hide">
          <button
            onClick={() => setSelectedCategory('all')}
            className={clsx(
              "px-3 py-1.5 text-xs font-medium rounded-lg transition-colors whitespace-nowrap",
              selectedCategory === 'all' 
                ? "bg-[#1E2937] text-white" 
                : "text-gray-600 hover:bg-gray-200"
            )}
          >
            Wszystkie
          </button>
          {Object.entries(FURNITURE_CATALOG).map(([key, category]) => (
            <button
              key={key}
              onClick={() => setSelectedCategory(key)}
              className={clsx(
                "px-3 py-1.5 text-xs font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-1",
                selectedCategory === key 
                  ? "bg-[#1E2937] text-white" 
                  : "text-gray-600 hover:bg-gray-200"
              )}
            >
              <span>{category.icon}</span>
              <span>{category.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Items List */}
      <div className="flex-1 overflow-y-auto h-96">
        {viewMode === 'grid' ? (
          <div className="p-3 grid grid-cols-2 gap-2">
            {filteredItems.map((item) => (
              <div 
                key={item.id}
                draggable
                onDragStart={(e) => handleDragStart(e, item)}
                onDragEnd={handleDragEnd}
                onClick={() => handleItemClick(item)}
                className={clsx(
                  "p-3 border-2 rounded-lg cursor-pointer transition-all hover:shadow-md",
                  isDragging?.id === item.id 
                    ? "border-[#1E2937] bg-gray-100 scale-95 opacity-50" 
                    : "border-gray-200 hover:border-gray-300 bg-white"
                )}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="text-2xl mb-2">{item.thumbnail}</div>
                  <div className="text-xs font-medium text-gray-900 line-clamp-2">
                    {item.name}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {item.dimensions.width}×{item.dimensions.depth}cm
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredItems.map((item) => (
              <div 
                key={item.id}
                draggable
                onDragStart={(e) => handleDragStart(e, item)}
                onDragEnd={handleDragEnd}
                onClick={() => handleItemClick(item)}
                className={clsx(
                  "p-3 cursor-pointer transition-all hover:bg-gray-50",
                  isDragging?.id === item.id 
                    ? "bg-gray-100 border-l-4 border-[#1E2937]" 
                    : "hover:border-l-4 hover:border-gray-300"
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="text-2xl flex-shrink-0">{item.thumbnail}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {item.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {item.dimensions.width}×{item.dimensions.depth}cm • {item.description}
                    </div>
                  </div>
                  <SafeIcon icon={FiPlus} className="text-gray-400 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="bg-gray-50 px-4 py-2 border-t border-gray-200">
        <div className="text-xs text-gray-500 text-center">
          Przeciągnij mebel na rzut aby dodać • {filteredItems.length} elementów
        </div>
      </div>
    </div>
  );
};

export default FurnitureCatalogPanel;